/* Video reviews carousel.
 *
 * Ported from the inline script on resembleperfumes.com, moved out of the
 * section so the theme editor's section reloads do not re-register listeners
 * on window every time.
 *
 * Three jobs:
 *   1. A transform carousel: arrows, touch swipe, mouse drag. Card width is
 *      computed from the viewport width divided by the visible column count,
 *      so the cards always fill the row exactly and no half card is left at
 *      the edge.
 *   2. Muted inline autoplay while a clip is on screen. This is the only kind
 *      of autoplay browsers allow without a gesture.
 *   3. Tap to unmute, which mutes every other clip first so two voices never
 *      overlap.
 *
 * The original bound mousemove/mouseup to window once per section and never
 * released them. Here every listener a section adds lives on that section's
 * own elements, except the drag-release pair, which is added on pointerdown
 * and removed on pointerup.
 */
(function () {
  'use strict';

  var MOBILE = 600;
  var TABLET = 1024;
  var SWIPE = 40;

  function init(root) {
    if (!root || root.dataset.resembleVideoReviewsBound === 'true') return;
    root.dataset.resembleVideoReviewsBound = 'true';

    var track = root.querySelector('.resemble-video-reviews__track');
    var viewport = root.querySelector('.resemble-video-reviews__viewport');
    if (!track || !viewport) return;

    var cards = Array.prototype.slice.call(
      track.querySelectorAll('.resemble-video-reviews__card')
    );
    if (!cards.length) return;

    var prevBtn = root.querySelector('.resemble-video-reviews__arrow--prev');
    var nextBtn = root.querySelector('.resemble-video-reviews__arrow--next');
    var desktopColumns = parseInt(root.dataset.columns, 10) || 4;
    var mobileColumns = root.dataset.twoUpOnMobile === 'true' ? 2 : 1;

    var current = 0;
    var cardWidth = 0;
    var didDrag = false;

    function columnCount() {
      var w = window.innerWidth;
      if (w <= MOBILE) return mobileColumns;
      if (w <= TABLET) return Math.min(3, desktopColumns);
      return desktopColumns;
    }

    function maxIndex() {
      return Math.max(0, cards.length - columnCount());
    }

    function goTo(n) {
      current = Math.min(Math.max(n, 0), maxIndex());
      track.style.transform = 'translateX(-' + current * cardWidth + 'px)';
      syncArrows();
    }

    function syncArrows() {
      if (prevBtn) prevBtn.setAttribute('aria-disabled', current <= 0 ? 'true' : 'false');
      if (nextBtn) {
        nextBtn.setAttribute('aria-disabled', current >= maxIndex() ? 'true' : 'false');
      }
    }

    function measure() {
      cardWidth = viewport.offsetWidth / columnCount();
      cards.forEach(function (card) {
        card.style.width = cardWidth + 'px';
      });
      goTo(current);
    }

    measure();
    window.addEventListener('resize', measure);

    // The aspect-ratio boxes settle only once the fonts and the first frame
    // land, and a stale cardWidth shows as a sliver of the next card. One
    // re-measure on load is cheaper than watching every video.
    window.addEventListener('load', measure);

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(current - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(current + 1);
      });
    }

    function step(delta) {
      if (Math.abs(delta) < SWIPE) {
        goTo(current);
        return;
      }
      goTo(delta > 0 ? current + 1 : current - 1);
    }

    var touchStartX = 0;
    track.addEventListener(
      'touchstart',
      function (event) {
        touchStartX = event.touches[0].clientX;
      },
      { passive: true }
    );

    track.addEventListener('touchend', function (event) {
      step(touchStartX - event.changedTouches[0].clientX);
    });

    var dragStartX = 0;
    var dragBase = 0;

    function onDragMove(event) {
      var delta = dragStartX - event.clientX;
      if (Math.abs(delta) > 5) didDrag = true;
      var limit = maxIndex() * cardWidth;
      var offset = Math.max(0, Math.min(dragBase + delta, limit));
      track.style.transform = 'translateX(-' + offset + 'px)';
    }

    function onDragEnd(event) {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      track.style.transition = '';
      step(dragStartX - event.clientX);
    }

    track.addEventListener('mousedown', function (event) {
      didDrag = false;
      dragStartX = event.clientX;
      dragBase = current * cardWidth;
      track.style.transition = 'none';
      event.preventDefault();
      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', onDragEnd);
    });

    // Capture phase: swallow the click that ends a drag before it reaches the
    // tap-to-unmute handler, so dragging past a clip does not also unmute it.
    track.addEventListener(
      'click',
      function (event) {
        if (didDrag) {
          event.preventDefault();
          event.stopPropagation();
          didDrag = false;
        }
      },
      true
    );

    var videos = Array.prototype.slice.call(
      track.querySelectorAll('.resemble-video-reviews__video')
    );

    if (videos.length && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              video.play().catch(function () {});
              return;
            }
            video.pause();
            if (!video.muted) {
              video.muted = true;
              var media = video.closest('.resemble-video-reviews__media');
              if (media) media.classList.remove('is-unmuted');
            }
          });
        },
        { threshold: 0.5 }
      );
      videos.forEach(function (video) {
        observer.observe(video);
      });
    }

    var tappable = Array.prototype.slice.call(
      track.querySelectorAll('.resemble-video-reviews__media--tappable')
    );

    if (tappable.length) {
      var toggle = function (media) {
        var video = media.querySelector('.resemble-video-reviews__video');
        if (!video) return;

        if (!video.muted) {
          video.muted = true;
          media.classList.remove('is-unmuted');
          return;
        }

        tappable.forEach(function (other) {
          if (other === media) return;
          var otherVideo = other.querySelector('.resemble-video-reviews__video');
          if (otherVideo) otherVideo.muted = true;
          other.classList.remove('is-unmuted');
        });

        video.muted = false;
        video.play().catch(function () {});
        media.classList.add('is-unmuted');
      };

      tappable.forEach(function (media) {
        media.addEventListener('click', function () {
          if (didDrag) return;
          toggle(media);
        });
        media.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle(media);
          }
        });
      });
    }
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll('.resemble-video-reviews')
      .forEach(function (root) {
        init(root);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
